/**
 * User Preferences Context
 *
 * Global user preferences that persist across sessions.
 * Stored in localStorage for persistence.
 *
 * NOTE: displayMode/legalese toggle was REMOVED (2026-03).
 * All terminology is now standardized on EDRM legal terminology.
 * SOPs and training materials require consistent terminology.
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import type { ThemeId } from '@/theme/types'

export type ThemeMode = 'dark' | 'light' | 'system'
export type DensityMode = 'compact' | 'normal' | 'relaxed'
export type TextScale = 'xs' | 'sm' | 'base' | 'lg' | 'xl'

export interface UserPreferences {
  /** Theme preference */
  theme: ThemeMode
  /** Named brand theme preference */
  themeId: ThemeId
  /** Global UI density */
  density: DensityMode
  /** Text size scale */
  textScale: TextScale
  /** Compact view for dense data display (Legacy - preferred density) */
  compactView: boolean
  /** Show methodology explanations by default */
  showMethodology: boolean
  /** Auto-expand search results */
  autoExpandResults: boolean
  /** Sidebar collapsed to icon rail */
  sidebarCollapsed: boolean
}

const TEXT_SCALE_VALUES: Record<TextScale, string> = {
  xs: '14px',
  sm: '15px',
  base: '16px',
  lg: '17px',
  xl: '18px',
}

const DEFAULT_PREFERENCES: UserPreferences = {
  theme: 'system',
  themeId: 'forensic',
  density: 'normal',
  textScale: 'base',
  compactView: false,
  showMethodology: true,
  autoExpandResults: false,
  sidebarCollapsed: false,
}

export { TEXT_SCALE_VALUES }

const STORAGE_KEY = 'eca-user-preferences'
const LEGACY_THEME_MODE_KEY = 'eca-ui-theme'
const LEGACY_THEME_ID_KEY = 'eca-ui-theme-id'

interface UserPreferencesContextValue {
  preferences: UserPreferences
  setPreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void
  resetPreferences: () => void
}

const UserPreferencesContext = createContext<UserPreferencesContextValue | null>(null)

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences>(() => {
    if (typeof window === 'undefined') return DEFAULT_PREFERENCES

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      const legacyTheme = localStorage.getItem(LEGACY_THEME_MODE_KEY) as ThemeMode | null
      const legacyThemeId = localStorage.getItem(LEGACY_THEME_ID_KEY) as ThemeId | null
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<UserPreferences>
        return {
          ...DEFAULT_PREFERENCES,
          ...parsed,
          theme: parsed.theme ?? legacyTheme ?? DEFAULT_PREFERENCES.theme,
          themeId: parsed.themeId ?? legacyThemeId ?? DEFAULT_PREFERENCES.themeId,
        }
      }
    } catch (e) {
      console.warn('[UserPreferences] Failed to load preferences:', e)
    }
    return DEFAULT_PREFERENCES
  })

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences))
    } catch (e) {
      console.warn('[UserPreferences] Failed to save preferences:', e)
    }
  }, [preferences])

  const setPreference = useCallback(<K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) => {
    setPreferences(prev => ({ ...prev, [key]: value }))
  }, [])

  const resetPreferences = useCallback(() => {
    setPreferences(DEFAULT_PREFERENCES)
  }, [])

  const value: UserPreferencesContextValue = {
    preferences,
    setPreference,
    resetPreferences,
  }

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  )
}

export function useUserPreferences(): UserPreferencesContextValue {
  const context = useContext(UserPreferencesContext)
  if (!context) {
    throw new Error('useUserPreferences must be used within UserPreferencesProvider')
  }
  return context
}

/**
 * @deprecated Display mode was removed. All text uses EDRM legal terminology.
 * This stub returns the first argument (legal term) for backward compatibility
 * during migration. Callers should be updated to use the legal term directly.
 */
export function useDisplayText() {
  return useCallback((legaleseText: string, _laymanText: string) => {
    return legaleseText
  }, [])
}

export default UserPreferencesContext
